import { ArrowLeft, ArrowRight, Award, X } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

import { useGetCertificationQuestionsQuery } from '@/@generated/codegen/API';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  useCertificationProgress,
  useInsertCertificationProgress,
  useUpdateCertificationProgress,
} from '@/hooks/queries/useCertificationProgress';
import { useMasterclassRatings } from '@/hooks/queries/useMasterclassRatings';
import { useToast } from '@/hooks/use-toast';
import { useTranslations } from '@/hooks/use-translations';
import { useCaseDetails } from '@/modules/case-selector/hooks/useCaseDetails';
import { trackEvent } from '@/modules/google-analytics/analytic/analytics';
import { MasterclassRatingModal } from '@/modules/masterclass-rating';
import { captureSentry } from '@/utils/sentryHelper';

import { CertificationResultModal } from './CertificationResultModal';
import { CERTIFICATION_CONFIG } from '../constants';

interface CertificationTestProps {
  /** Database ID of the case */
  caseId: string;
  /** Masterclass ID for progress tracking */
  masterclassId?: string;
  userId: string;
  onClose: () => void;
  onTestComplete?: () => void;
}

export const CertificationTest = ({
  caseId,
  masterclassId,
  userId,
  onClose,
  onTestComplete,
}: CertificationTestProps) => {
  const { t } = useTranslations();

  // caseId is now the database ID, use it directly
  const { details: caseDetails, loading: caseDetailsLoading } = useCaseDetails(caseId);

  // Fetch certification questions from backend
  const {
    data: certificationData,
    loading: certificationLoading,
    error: certificationError,
  } = useGetCertificationQuestionsQuery({
    variables: {
      input: { patientCaseId: caseId },
    },
    skip: !caseId,
  });

  const questions = certificationData?.getCertificationQuestions?.certification_questions ?? [];

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>([]);
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [score, setScore] = useState(0);
  const [passed, setPassed] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState(0);
  const { toast } = useToast();

  // Track if we've already initialized the attempt
  const attemptInitialized = useRef(false);

  // Apollo hooks
  const { data: progressData } = useCertificationProgress(userId, caseId, masterclassId || null);
  const { data: ratingsData } = useMasterclassRatings(caseId, masterclassId || null);
  const insertProgress = useInsertCertificationProgress();
  const updateProgress = useUpdateCertificationProgress();

  const isLoading = certificationLoading || caseDetailsLoading;

  // Initialize answers array when questions load
  useEffect(() => {
    if (questions.length > 0 && answers.length === 0) {
      setAnswers(new Array(questions.length).fill(null));
    }
  }, [questions.length, answers.length]);

  // Initialize or restore attempt when data is available
  useEffect(() => {
    if (attemptInitialized.current || questions.length === 0 || progressData === undefined) {
      return;
    }

    const initializeAttempt = async () => {
      attemptInitialized.current = true;

      // Check for existing in-progress attempt
      const inProgressAttempt = progressData?.find((p) => p.completedAt === null);

      if (inProgressAttempt) {
        // Restore existing progress
        setAttemptId(inProgressAttempt.id);
        setCurrentQuestionIndex(inProgressAttempt.currentQuestion ?? 0);
        const savedAnswers = Array.isArray(inProgressAttempt.answers)
          ? inProgressAttempt.answers.map((a) => (typeof a === 'number' ? a : null))
          : new Array(questions.length).fill(null);
        setAnswers(savedAnswers);
      } else {
        // Calculate next attempt number
        const completedAttempts = progressData?.filter((p) => p.completedAt !== null) || [];
        const nextAttemptNumber =
          completedAttempts.length > 0
            ? Math.max(...completedAttempts.map((a) => a.attemptNumber)) + 1
            : 1;

        try {
          // Create new in-progress attempt
          const newProgress = await insertProgress.mutateAsync({
            userId: userId,
            caseId: caseId,
            masterclassId: masterclassId || '',
            attemptNumber: nextAttemptNumber,
            currentQuestion: 0,
            answers: [],
          });

          if (newProgress) {
            setAttemptId(newProgress.id);
          }
        } catch (error) {
          console.error('Error creating certification attempt:', error);
          captureSentry({
            title: 'Failed to create certification attempt',
            error,
            detail: { caseId, userId },
          });
          toast({
            title: 'Error',
            description: t('validation.errors.loadingCertification'),
            variant: 'destructive',
          });
        }
      }
    };

    void initializeAttempt();
  }, [questions.length, progressData, caseId, userId, masterclassId, insertProgress, t, toast]);

  // Show error if certification failed to load
  useEffect(() => {
    if (certificationError) {
      console.error('Error loading certification:', certificationError);
      captureSentry({
        title: 'Failed to load certification',
        error: certificationError,
        detail: { caseId, userId },
      });
      toast({
        title: 'Error',
        description: t('validation.errors.loadingCertification'),
        variant: 'destructive',
      });
    }
  }, [certificationError, caseId, userId, t, toast]);

  const saveProgress = async (questionIndex: number, newAnswers: (number | null)[]) => {
    if (!attemptId) return;

    await updateProgress.mutateAsync({
      id: attemptId,
      updates: {
        currentQuestion: questionIndex,
        answers: newAnswers,
      },
    });
  };

  const handleAnswerSelect = (answerIndex: number) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestionIndex] = answerIndex;
    setAnswers(newAnswers);
    void saveProgress(currentQuestionIndex, newAnswers);
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      void saveProgress(nextIndex, answers);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      const prevIndex = currentQuestionIndex - 1;
      setCurrentQuestionIndex(prevIndex);
      void saveProgress(prevIndex, answers);
    }
  };

  const handleSubmit = async () => {
    if (!attemptId) return;

    try {
      // Calculate score
      const correctAnswers = answers.filter(
        (answer, index) => answer === questions[index].correct_answer
      ).length;

      const finalScore = (correctAnswers / questions.length) * 100;
      const testPassed = finalScore >= CERTIFICATION_CONFIG.PASSING_SCORE_CERTIFICATION;

      trackEvent('Certification', 'Finish Certification', caseId, Math.round(finalScore));

      // Mark the attempt as completed
      await updateProgress.mutateAsync({
        id: attemptId,
        updates: {
          score: finalScore,
          passed: testPassed,
          completedAt: new Date().toISOString(),
        },
      });

      // Calculate remaining attempts for the failure modal
      const completedAttempts = progressData?.filter((p) => p.completedAt !== null) || [];
      const attemptsUsed = completedAttempts.length;
      const remaining = Math.max(0, CERTIFICATION_CONFIG.MAX_ATTEMPTS - attemptsUsed);

      // Check if user has already rated this masterclass (regardless of attempt)
      const existingRating = ratingsData?.find((r) => r.userId === userId);

      setScore(finalScore);
      setPassed(testPassed);
      setRemainingAttempts(remaining);

      // Show rating if not already done, otherwise show results
      if (!existingRating) {
        setShowRating(true);
      } else {
        setShowResults(true);
      }
    } catch (error) {
      console.error('Error submitting certification:', error);
      captureSentry({
        title: 'Failed to submit certification',
        error,
        detail: { caseId, userId, attemptId },
      });
      toast({
        title: 'Error',
        description: 'Unable to submit certification',
        variant: 'destructive',
      });
    }
  };

  const handleCloseResults = () => {
    setShowResults(false);
    if (onTestComplete) onTestComplete();
    onClose();
  };

  if (isLoading || !caseDetails) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <Card className="p-8">
          <p className="font-body text-lg">{t('certification.test.loadingTest')}</p>
        </Card>
      </div>
    );
  }

  if (certificationError || questions.length === 0) {
    return null;
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
  const allAnswered = answers.every((a) => a !== null);

  return (
    <>
      {!showRating && !showResults && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="max-h-[90vh] w-full max-w-3xl overflow-y-auto p-6">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-heading text-3xl text-livelinx-purple200">
                <Award className="mr-2 inline h-8 w-8" />
                {t('certification.test.title', {
                  caseTitle: caseDetails.displayName,
                })}
              </h2>
              <Button variant="ghost" onClick={onClose} size="icon">
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="mb-6">
              <div className="mb-2 flex justify-between text-sm text-livelinx-grey200">
                <span>
                  {t('certification.test.questionProgress', {
                    current: currentQuestionIndex + 1,
                    total: questions.length,
                  })}
                </span>
                <span>
                  {t('certification.test.progressComplete', {
                    percent: progress.toFixed(0),
                  })}
                </span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            <div className="mb-8">
              <h3 className="mb-4 font-body text-xl text-livelinx-text">
                {currentQuestion?.question_text}
              </h3>

              <RadioGroup
                value={answers[currentQuestionIndex]?.toString() || ''}
                onValueChange={(value) => handleAnswerSelect(parseInt(value))}
              >
                {currentQuestion?.options.map((option, index) => (
                  <div
                    key={index}
                    className="mb-3 flex items-center space-x-3 rounded-lg border border-livelinx-lightgray100 p-4 transition-colors hover:bg-livelinx-purple50"
                  >
                    <RadioGroupItem value={index.toString()} id={`option-${index}`} />
                    <Label
                      htmlFor={`option-${index}`}
                      className="flex-1 cursor-pointer font-body text-base"
                    >
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="flex items-center justify-between border-t border-livelinx-lightgray100 pt-4">
              <Button
                onClick={handlePrevious}
                disabled={currentQuestionIndex === 0}
                variant="outline"
                className="border-livelinx-purple200 text-livelinx-purple200"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('common.buttons.previous')}
              </Button>

              {currentQuestionIndex === questions.length - 1 ? (
                <Button
                  onClick={() => void handleSubmit()}
                  disabled={!allAnswered}
                  className="bg-livelinx-purple200 text-white hover:bg-livelinx-purple400"
                >
                  {t('certification.test.submitTest')}
                  <Award className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  disabled={
                    answers[currentQuestionIndex] === null ||
                    answers[currentQuestionIndex] === undefined
                  }
                  className="bg-livelinx-purple200 text-white hover:bg-livelinx-purple400"
                >
                  {t('common.buttons.next')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
            </div>
          </Card>
        </div>
      )}

      {showRating && attemptId && (
        <MasterclassRatingModal
          caseId={caseId}
          userId={userId}
          masterclassId={masterclassId || ''}
          certificationProgressId={attemptId}
          isOpen={showRating}
          onRatingSubmitted={() => {
            setShowRating(false);
            setShowResults(true);
          }}
        />
      )}

      {showResults && (
        <CertificationResultModal
          score={score}
          passed={passed}
          caseId={caseId}
          caseTitle={caseDetails.displayName}
          onClose={handleCloseResults}
          remainingAttempts={remainingAttempts}
        />
      )}
    </>
  );
};
